import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  Button,
  Typography,
  FormHelperText,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import colors from '../../../../utils/colors';
import { GreenSelectForm } from '../../../common/Greens';

const menuProps = {
  PaperProps: {
    sx: {
      '& .MuiMenuItem-root': { fontSize: 14 },
    },
  },
};

const ProjectRoleRepeater = ({
  projectRoles,
  onChange,
  projects,
  roles,
  errors,
  touched,
  disabled = false,
  helperText = "Assign roles to projects for this user. Role and project assignment are both optional.",
}) => {
  const handleAddRow = () => {
    onChange([...projectRoles, { projectId: '', roleId: '' }]);
  };

  const handleRemoveRow = (index) => {
    const newProjectRoles = projectRoles.filter((_, i) => i !== index);
    if (newProjectRoles.length === 0) {
      onChange([{ projectId: '', roleId: '' }]);
    } else {
      onChange(newProjectRoles);
    }
  };

  const handleFieldChange = (index, field, value) => {
    const newProjectRoles = [...projectRoles];
    newProjectRoles[index] = {
      ...newProjectRoles[index],
      [field]: value,
    };
    onChange(newProjectRoles);
  };

  const getUsedProjectIds = (excludeIndex) => projectRoles
    .filter((_, i) => i !== excludeIndex)
    .map((pr) => pr.projectId)
    .filter((id) => id);

  const showRepeaterError = Boolean(touched && errors);
  const repeaterErrorText = typeof errors === 'string'
    ? errors
    : 'Please complete all project-role assignments';

  return (
    <Box>
      <Stack spacing={2}>
        {projectRoles.map((projectRole, index) => {
          const usedProjectIds = getUsedProjectIds(index);
          const availableProjects = projects.filter(
            (p) => !usedProjectIds.includes(p.id) || p.id === projectRole.projectId,
          );
          const projectLabelId = `project-role-project-${index}`;
          const roleLabelId = `project-role-role-${index}`;

          return (
            <Stack
              key={index}
              direction="row"
              spacing={2}
              alignItems="flex-end"
              sx={{
                p: 2,
                backgroundColor: colors.neutral[50],
                borderRadius: '8px',
                border: `1px solid ${colors.neutral[200]}`,
              }}
            >
              <GreenSelectForm fullWidth variant="outlined" sx={{ flex: 1, m: 0 }}>
                <InputLabel id={projectLabelId} shrink>
                  Project
                </InputLabel>
                <Select
                  labelId={projectLabelId}
                  label="Project"
                  value={projectRole.projectId || ''}
                  onChange={(e) => handleFieldChange(index, 'projectId', e.target.value)}
                  disabled={disabled}
                  displayEmpty
                  MenuProps={menuProps}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography sx={{ fontSize: 14, color: colors.neutral[400] }}>
                          Select project
                        </Typography>
                      );
                    }
                    const selectedProject = availableProjects.find((p) => p.id === selected);
                    return (
                      <Typography
                        sx={{
                          fontSize: 14,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {selectedProject ? selectedProject.name : ''}
                      </Typography>
                    );
                  }}
                >
                  <MenuItem value="" disabled>
                    <Typography sx={{ fontSize: 14, color: colors.neutral[400] }}>
                      Select project
                    </Typography>
                  </MenuItem>
                  {availableProjects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      <Typography sx={{ fontSize: 14 }}>{project.name}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </GreenSelectForm>

              <GreenSelectForm fullWidth variant="outlined" sx={{ flex: 1, m: 0 }}>
                <InputLabel id={roleLabelId} shrink>
                  Role
                </InputLabel>
                <Select
                  labelId={roleLabelId}
                  label="Role"
                  value={projectRole.roleId || ''}
                  onChange={(e) => handleFieldChange(index, 'roleId', e.target.value)}
                  disabled={disabled || !projectRole.projectId}
                  displayEmpty
                  MenuProps={menuProps}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography sx={{ fontSize: 14, color: colors.neutral[400] }}>
                          Select role
                        </Typography>
                      );
                    }
                    const selectedRole = roles.find((r) => (r.value || r.id) === selected);
                    return (
                      <Typography
                        sx={{
                          fontSize: 14,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {selectedRole ? (selectedRole.label || selectedRole.name) : ''}
                      </Typography>
                    );
                  }}
                >
                  <MenuItem value="" disabled>
                    <Typography sx={{ fontSize: 14, color: colors.neutral[400] }}>
                      Select role
                    </Typography>
                  </MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.value || role.id} value={role.value || role.id}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{ fontSize: 14 }}>
                          {role.label || role.name}
                        </Typography>
                        {role.isCustom && (
                          <Box
                            component="span"
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: '4px',
                              backgroundColor: '#EEF0FE',
                              color: '#6371F7',
                              fontSize: 12,
                              fontWeight: 500,
                            }}
                          >
                            Custom
                          </Box>
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </GreenSelectForm>

              <IconButton
                onClick={() => handleRemoveRow(index)}
                disabled={disabled || projectRoles.length === 1}
                sx={{
                  mb: 0.5,
                  color: colors.neutral[400],
                  '&:hover': {
                    color: colors.red[100],
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  },
                  '&:disabled': {
                    color: colors.neutral[200],
                  },
                }}
                aria-label="Remove project assignment"
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          );
        })}

        <Button
          onClick={handleAddRow}
          disabled={disabled}
          startIcon={<AddIcon />}
          variant="outlined"
          sx={{
            fontSize: 14,
            fontWeight: 500,
            textTransform: 'none',
            color: '#6371F7',
            borderColor: '#6371F7',
            borderRadius: '10px',
            px: 2,
            height: 40,
            '&:hover': {
              borderColor: '#4B59E3',
              backgroundColor: 'rgba(99, 113, 247, 0.05)',
              color: '#6371F7',
            },
            alignSelf: 'flex-start',
          }}
        >
          Add Project
        </Button>

        {showRepeaterError ? (
          <FormHelperText error sx={{ fontSize: 12, mx: 0 }}>
            {repeaterErrorText}
          </FormHelperText>
        ) : (
          <Typography sx={{ fontSize: 12, color: colors.neutral[500] }}>
            {helperText}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

ProjectRoleRepeater.propTypes = {
  projectRoles: PropTypes.arrayOf(
    PropTypes.shape({
      projectId: PropTypes.string,
      roleId: PropTypes.string,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  projects: PropTypes.array.isRequired,
  roles: PropTypes.array.isRequired,
  errors: PropTypes.any,
  touched: PropTypes.bool,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
};

ProjectRoleRepeater.defaultProps = {
  projectRoles: [{ projectId: '', roleId: '' }],
  disabled: false,
};

export default ProjectRoleRepeater;
